import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  TablePagination,
  TextField,
  Link,
  Tab,
  Tabs,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';  // Fixed the import path
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../config/permissions';

export default function ProjectManagement() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [projects, setProjects] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState('active');

  const canEditProject = hasPermission(PERMISSIONS.EDIT_PROJECT);
  const canDeleteProject = hasPermission(PERMISSIONS.DELETE_PROJECT);

  useEffect(() => {
    fetchProjects();
  }, [page, rowsPerPage, search, activeTab]);

  const fetchProjects = async () => {
    try {
      let query = supabase
        .from('projects')
        .select('*', { count: 'exact' });

      if (search) {
        query = query.ilike('name', `%${search}%`);
      }
      
      // Filter based on active tab
      if (activeTab === 'active') {
        query = query.neq('status', 'archived');
      } else {
        query = query.eq('status', 'archived');
      }

      const { data, count, error } = await query
        .range(page * rowsPerPage, (page + 1) * rowsPerPage - 1)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setProjects(data || []);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Weet je zeker dat je dit project wilt verwijderen?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (error) throw error;
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <TextField
        fullWidth
        variant="outlined"
        label="Zoeken op projectnaam"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Projectbeheer</Typography>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(e, newValue) => {
          setActiveTab(newValue);
          setPage(0);
        }}
        sx={{ mb: 2 }}
      >
        <Tab label="Actieve Projecten" value="active" />
        <Tab label="Gearchiveerde Projecten" value="archived" />
      </Tabs>

      {/* Button Section */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/projects/new')}
          startIcon={<AddIcon />}
        >
          Nieuw Project Toevoegen
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Projectnaam</TableCell>
              <TableCell>Aangemaakt op</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Acties</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.map((project) => (
              <TableRow key={project.id}>
                <TableCell>
                  <Link
                    component="button"
                    variant="body1"
                    onClick={() => navigate(`/projects/${project.id}/settings`)}
                    sx={{
                      textAlign: 'left',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                        cursor: 'pointer'
                      }
                    }}
                  >
                    {project.name}
                  </Link>
                </TableCell>
                <TableCell>
                  {new Date(project.created_at).toLocaleDateString('nl-NL')}
                </TableCell>
                <TableCell>
                  <Chip
                    label={project.status || 'Actief'}
                    color={project.status === 'archived' ? 'warning' : 'success'}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={() => navigate(`/projects/${project.id}/settings`)}
                  >
                    <EditIcon />
                  </IconButton>
                  {canDeleteProject && (
                    <IconButton
                      onClick={() => handleDeleteProject(project.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Rijen per pagina"
        />
      </TableContainer>
    </Box>
  );
}